import React, { useState } from 'react'
import styled from 'styled-components'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import HEHMQuantityBox from './HEHMQuantityBox'
import LabelValuePair from 'pages/paywall/LabelValuePair'

import { shortDate } from 'helpers/formatters'
import { BillingData } from 'types/BillingData'
import { PaywallAcct } from 'types/PaywallAcct'
import { useNavigate, useParams } from 'react-router-dom'
import HEHMTermsAndConditionsModal from 'pages/paywall/HEHMTermsAndConditionsModal'
import GA4 from 'helpers/ga4'
import Brand from 'helpers/brand'
import { useFeaturesContext } from 'hooks/FeaturesContext'

interface HEHMAccountSummaryProps {
  activeBillingData: BillingData
  activeHehmAccount: boolean
  hehmAccountUpdatedAt: string | undefined
  hehmQuantity: number | undefined
  isHehmQuantityLoaded: boolean
  inactiveHehmAccount: boolean
  channel: string
  premiumAccount: PaywallAcct | undefined
  cancelPlanRenewalClickHandler: () => void
  changeBillingInfoClickHandler: () => void
  changeCreditCardInfoClickHandler: () => void
}

const HEHMAccountSummary = ({
  activeBillingData,
  activeHehmAccount,
  hehmAccountUpdatedAt,
  hehmQuantity,
  isHehmQuantityLoaded,
  inactiveHehmAccount,
  channel,
  premiumAccount,
  cancelPlanRenewalClickHandler,
  changeBillingInfoClickHandler,
  changeCreditCardInfoClickHandler
}: HEHMAccountSummaryProps) => {
  const { dealerId } = useParams()
  const navigate = useNavigate()
  const [displayTermsModal, setDisplayTermsModal] = useState(false)
  const { enabledFeatures } = useFeaturesContext()

  const sendGA4Event = (label: string, category = 'PMK Settings') => {
    GA4.sendEvent({ action: 'Click PMK Account Settings Option', category, label: `Button: ${label}` })
  }

  const onTermsClick = () => {
    sendGA4Event('T&Cs')
    setDisplayTermsModal(true)
  }

  const onStatementsClick = () => {
    sendGA4Event('Statements')
    navigate(`/ehmkt/statements/${dealerId}`)
  }

  const onCancelPlanClick = () => {
    sendGA4Event('Cancel Plan', 'Account Settings')
    cancelPlanRenewalClickHandler()
  }

  const onChangeCreditCardClick = () => {
    sendGA4Event('Change Credit Card', 'Account Settings')
    changeCreditCardInfoClickHandler()
  }

  const onUpdateBillingInfoClick = () => {
    sendGA4Event('Change Billing Info', 'Account Settings')
    changeBillingInfoClickHandler()
  }

  return (
    <S.Form $borderColor={color('hehmAccent')} $matchHeight={activeHehmAccount}>
      <S.ProductLogo src={Brand.hehmProductLogoPath()} alt="HEHM system logo" />
      <S.InnerContainer>
        <S.Explanation>Account Information & Billing Summary</S.Explanation>
        {activeBillingData?.firstName && activeBillingData?.lastName ? <S.LabelValuePair key={activeBillingData?.firstName} label={'Name'} value={`${activeBillingData?.firstName} ${activeBillingData?.lastName}`} /> : null}
        {activeBillingData?.email && <S.LabelValuePair key={activeBillingData?.email} label={'Email'} value={activeBillingData?.email} />}
        {activeBillingData?.address && <S.LabelValuePair label={'Address'} value={activeBillingData?.address} />}
        {activeBillingData?.city && activeBillingData?.state && activeBillingData?.zip &&
          <S.AddressSecondLine>
            {[activeBillingData?.city, activeBillingData?.state, activeBillingData?.zip].join(', ')}
          </S.AddressSecondLine>
        }
        <p>You will be charged $6 plus applicable taxes per installed kit.</p>
        { !inactiveHehmAccount && (
          channel === 'DSO' ? (
            <Button
              decoration="link"
              text="Update billing information"
              styles={secondaryButtonStyle}
              onPress={onUpdateBillingInfoClick}
            />
          ) : (
            <Button
              decoration="link"
              text="Change credit card information"
              styles={secondaryButtonStyle}
              onPress={onChangeCreditCardClick}
            />
          )
        )}
        <S.InnerContainer>
          <Button decoration="link" text="Statements" styles={secondaryButtonStyle} onPress={onStatementsClick} />
        </S.InnerContainer>
        <HEHMQuantityBox hehmQuantity={hehmQuantity} hehmAccountUpdatedAt={hehmAccountUpdatedAt} isHehmQuantityLoaded={isHehmQuantityLoaded} />
        <S.Line />
        <>
          {inactiveHehmAccount &&
            <>
              {/* TODO: Hook up onPress once reactivation functionality is built for HEHM plans  */}
              <Button decoration="link" text="Enable account" styles={secondaryButtonStyle} onPress={() => navigate(`/ehmkt/options/${dealerId}`)} />
              <S.InactiveLabel>Inactive since {shortDate(premiumAccount?.updatedAt)}</S.InactiveLabel>
            </>
          }
          {!inactiveHehmAccount &&
            <S.PlanLinkAndNoteContainer>
              <Button decoration="link" text="Cancel plan" styles={secondaryButtonStyle} onPress={onCancelPlanClick} />
              <S.ActionLinkHelperNote>(cancellation applies immediately)</S.ActionLinkHelperNote>
            </S.PlanLinkAndNoteContainer>
          }
          {channel === 'DSO' && <S.ItalicNote>Each installed kit is billed through Oracle</S.ItalicNote>}
          {!enabledFeatures?.includes('beta_tcs') && (
            <S.HehmTermsButtonContainer>
              <Button decoration="secondary" text="Terms & Conditions" styles={termsButtonStyle} onPress={onTermsClick} />
            </S.HehmTermsButtonContainer>
          )}
        </>
      </S.InnerContainer>
      {displayTermsModal &&
        <HEHMTermsAndConditionsModal
          closeCallback={() => setDisplayTermsModal(false)}
          hehmTermsUpToDate
        />
      }
    </S.Form>
  )
}

const secondaryButtonStyle: ButtonStyles = {
  button: { paddingLeft: 0, alignSelf: 'flex-start', marginTop: 10 },
  text: { fontSize: 16, textTransform: 'none' }
}

const termsButtonStyle: ButtonStyles = {
  button: { borderColor: color('hehmAccent'), borderRadius: 10 },
  text: { color: color('hehmAccent'), textTransform: 'none' }
}

export default HEHMAccountSummary

const S = {
  AddressSecondLine: styled.div`
    font-size: 16px;
    color: ${color('premiumText')};
  `,
  ActionLinkHelperNote: styled.span`
    font-size: 12px;
    margin-top: 10px;
    width: 100%;
    color: ${color('premiumText')};
  `,
  HehmTermsButtonContainer: styled.div`
    align-self: center;
    margin-top: 20px;
  `,
  InactiveLabel: styled.div`
    font-size: 12px;
    width: 100%;
    color: ${color('premiumText')};
  `,
  ItalicNote: styled.span`
    font-style: italic;
  `,
  LabelValuePair: styled(LabelValuePair)`
    margin-top: 15px;
  `,
  Line: styled.div`
    border: 1.5px solid ${color('border')};
    width: 100%;
    margin-top: 25px;
    margin-bottom: 25px;
  `,
  Explanation: styled.h2`
    display: flex;
    margin-bottom: 10px;
    text-align: left;
    text-transform: none;
    font-size: 24px;
  `,
  Form: styled.div<{ $borderColor?: string; $matchHeight?: boolean }>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-content: flex-start;
    border-radius: 10px;
    background-color: ${color('billingAddressBox')};
    padding: 15px;
    max-width: fit-content;
    border: 1px solid ${(props) => props.$borderColor || 'transparent'};
    ${(props) => props.$matchHeight && `
      height: 100%;
    `}
    width: 425px;

    @media ${screenSize.medium} {
        width: 350px;
        height: 100%;
    }
  `,
  InnerContainer: styled.div`
    display: flex;
    flex-direction: column;
    width: 96%;
    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  PlanLinkAndNoteContainer: styled.div`
    display: flex;
    align-items: center;
  `,
  ProductLogo: styled.img`
    width: 200px;
    margin-bottom: 2rem;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `
}
